/* cc-set-builder.js - 15 October 2026

   Charly's set builder: pick two photographs or more, see them on a real
   measured wall at true scale, add them to the cart as ordinary line items.

   THE ONE IDEA. A print's size on screen is its FRAMED size in centimetres
   multiplied by the room's own px-per-cm, expressed as a percentage of the
   plate width. Percentages rather than pixels, so the preview stays true to
   scale at any viewport without a resize listener.

   WHY THE FRAMED SIZE AND NOT THE PRINT SIZE. A box frame adds 20mm of face
   on every edge, measured by Charly on 27 Sep, so a 60x90 print hangs as
   64x94. The wall sees the frame. Using the print size would under-draw every
   frame by 4cm and under-count the row by 4cm per print, which on a 167cm
   wall is the difference between fitting and not.

   ORIENTATION SWAPS THE PAIR. The size tokens are stored portrait; a
   landscape photograph is the same frame turned, so 64x94 becomes 94x64.
   Getting this wrong is the near-miss CLAUDE.md records: a landscape 60x90
   telling the customer 60 wide when it arrives 90 wide.

   THE WALL IS FINITE. Usable wall is 167cm in two rooms and 210cm in the
   rest. Charly's instruction, 15 Oct: switch room automatically. So when a
   selection outgrows the current room this moves to one that fits and says
   so. Two framed 100x150 need 217cm and fit NO room - that case cannot be
   rescued by switching, so it is called out as a limit of the PREVIEW, not of
   the product. The set is still buyable; it just cannot be photographed side
   by side on any wall that exists.

   Variants and prices are fetched per product from /products/<handle>.js on
   first use and cached, so the page ships without 1,800 variants in it. */
(function () {
  'use strict';

  var root = document.querySelector('[data-cc-set-builder]');
  if (!root) return;

  /* framed outer size per stored token, portrait. From snippets/cc-framed-cm:
     the print plus 2cm on each edge. Keep in step with that file. */
  var FRAMED = {
    '20x30': [24, 34], '30x46': [34, 49], '41x61': [44, 64],
    '61x91': [64, 94], '81x122': [84, 124], '102x152': [104, 154],
  };
  /* what the customer sees, which is round. snippets/cc-size-cm is the single
     place that maps stored to displayed; this mirrors it for the control. */
  var SHOWN = {
    '20x30': '20 × 30', '30x46': '30 × 45', '41x61': '40 × 60',
    '61x91': '60 × 90', '81x122': '80 × 120', '102x152': '100 × 150',
  };
  var ORDER = ['20x30', '30x46', '41x61', '61x91', '81x122', '102x152'];

  var GAP_CM = parseFloat(root.getAttribute('data-gap-cm')) || 9;
  /* Charly, 17 Oct: "if someone want to add 6 should be possible accros all
     sizes but can't be over each other must have 1-2cm spacing safety."
     So the gap is no longer fixed. It stays at the preferred 9cm while the
     row fits, then closes down to MIN_GAP_CM to make a wider set fit a
     narrower wall, and never below it: prints that touch read as one object
     and a real hang needs a hand between them. Below the floor the row is
     genuinely too wide and the note says so rather than overlapping, which
     is the one outcome he ruled out. */
  var MIN_GAP_CM = 2;
  var MAX_SLOTS = 6;
  /* the three Prodigi box-frame colours, in the order the controls show
     them. One list, because the row control, the url parser and the
     wall all have to agree on what a colour is called. */
  var COLOURS = ['black', 'white', 'natural'];
  /* the 2cm of frame face on every edge that turns a print size into the
     framed outer in FRAMED above. Named once so AR can subtract it back. */
  var FRAME_FACE_CM = 2;
  /* the moulding band Mockup Crea rendered: a 20mm face plus the 8mm
     rebate lip that overlaps the print. The listed size is the glaze, so
     the object on the wall is this much bigger on every edge. */
  var MOULDING_CM = 2.8;

  var el = {
    stage: root.querySelector('[data-setb-stage]'),
    plate: root.querySelector('[data-setb-plate]'),
    wall: root.querySelector('[data-setb-wall]'),
    front: root.querySelector('[data-setb-front]'),
    note: root.querySelector('[data-setb-note]'),
    msg: root.querySelector('[data-setb-msg]'),
    roomlist: root.querySelector('[data-setb-roomlist]'),
    positions: root.querySelector('[data-setb-positions]'),
    total: root.querySelector('[data-setb-total]'),
    add: root.querySelector('[data-setb-add]'),
    ar: root.querySelector('[data-setb-ar]'),
    chooser: root.querySelector('[data-setb-chooser]'),
    frames: root.querySelector('[data-setb-frames]'),
    thumb: root.querySelector('[data-setb-thumb]'),
    roomfact: root.querySelector('[data-setb-roomfact]'),
    compare: root.querySelector('[data-setb-compare]'),
    comparebtn: root.querySelector('[data-setb-comparebtn]'),
    tidy: root.querySelector('[data-setb-tidy]'),
    hint: root.querySelector('[data-setb-hint]'),
  };

  var PRODUCTS = [];
  try {
    PRODUCTS = JSON.parse(root.querySelector('[data-setb-products]').textContent) || [];
  } catch (e) { return; }
  if (!PRODUCTS.length) return;

  var ROOMS = null;
  var state = { room: null, colour: 'black', slots: [], free: false };

  /* 22 Oct 2026, cc-marketing GA4 spec (17 Oct): "diy_room_selected with the
     room key, and the room key carried through to add_to_cart". Same route as
     snippets/cc-lead-event.liquid: Shopify's analytics bus, and a dataLayer if
     one exists. GA4 only counts it once a custom pixel in Shopify admin
     (Settings > Customer events) subscribes to these names, which is Charly's
     to set up. Until then this publishes into the void, harmlessly. No
     personal data: a room key and a count. */
  function track(name, payload) {
    try { if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') Shopify.analytics.publish(name, payload); } catch (e) {}
    try { window.dataLayer = window.dataLayer || []; window.dataLayer.push(Object.assign({ event: name }, payload)); } catch (e) {}
  }

  /* ------------------------------------------------ SETS AND COMPOSE MODE
     22 Oct 2026. Charly: "push the diy room build to edit my own compo as I
     want to update them and add some".

     The sets used to live as a `start` list on each room in cc-studio-rooms.json
     (one set per room, six rooms). They now live in assets/cc-diy-sets.json: a
     set is a room key, a default frame colour and a list of prints, optionally
     with the positions they were dragged to. Any number of sets, any room.
     applySet() still falls back to a room's own `start` for a key that is not a
     set id, so every old link (?start=<room>) keeps working.

     COMPOSE MODE is /pages/diy-room?compose=1. It adds a panel under the
     builder where a composition can be named, saved IN THIS BROWSER (it then
     shows as a tile marked "yours" on the set row, for this browser only) and
     exported as JSON to be sent for adding to the site. Nothing in compose mode
     writes to the store, and a visitor without the parameter never sees it. */
  var SETS = [];
  var COMPOSE = /[?&]compose=1(&|$)/.test(location.search);
  var LS_KEY = 'cc_my_sets_v1';
  var onPaint = null;
  function mySets() {
    if (!COMPOSE) return [];
    try { return JSON.parse(localStorage.getItem(LS_KEY) || '[]') || []; } catch (e) { return []; }
  }
  function saveMySets(a) { try { localStorage.setItem(LS_KEY, JSON.stringify(a)); } catch (e) {} }
  function findSet(id) {
    var all = mySets().concat(SETS);
    for (var i = 0; i < all.length; i++) if (all[i].id === id) return all[i];
    return null;
  }
  function slugOf(t) { return String(t || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'my-set'; }
  var readyResolve = null;

  /* FRAME COLOUR IS PER PRINT, 17 Oct. Charly: "frames colors can be
     customized for each photo mixing frames it's possibles."

     state.colour is no longer the colour of the set. It is the default a
     new slot is born with and what the FRAME control above the wall sets
     on all of them at once; a slot that has been given its own colour
     keeps it. Nothing about the cart changes shape, because the colour
     was always carried in the variant id and the SKU is already per item
     ({handle}-FR-{BLK|WHT|NAT}-{SIZE}): mixing frames was always sellable,
     the builder just could not express it.

     Everywhere that used to read state.colour for a SLOT now reads
     colourOf(slot). The two meanings were the same value until today and
     telling them apart is the whole change. */
  function colourOf(s) { return (s && s.colour) || state.colour; }

  /* one place that turns a slot into its variant, so a colour or size
     change cannot resolve one way here and another way there */
  function resolveVariant(s) {
    var by = s.handle && cache[s.handle] && cache[s.handle][colourOf(s)];
    var v = s.size && by && by[s.size];
    s.variantId = v ? v.id : null;
    s.price = v ? v.price : 0;
    return v;
  }
  var cache = {};

  /* ---------------------------------------------------------------- data */

  /* A HANDLE MAY BE THE OLD ONE OR THE NEW ONE, 19 Oct 2026. Charly asked for
     photo names in product URLs, so /products/dji-0817 becomes
     /products/dji-0817-green-island-the-spit-crossing. The new handle is the
     old one with a suffix, and the old one is what the room data, the shared
     set links and the stored start sets all still carry. This returns the
     handle of the product that actually exists, whichever form it is given,
     so none of them needs rewriting and none breaks on either side of the
     rename. The match is exact first and prefix second, and the prefix must
     end at a hyphen so dji-0817 can never match dji-08170. */
  function resolveHandle(h) {
    if (!h) return h;
    for (var i = 0; i < PRODUCTS.length; i++) if (PRODUCTS[i].handle === h) return h;
    for (var j = 0; j < PRODUCTS.length; j++) if (PRODUCTS[j].handle.indexOf(h + '-') === 0) return PRODUCTS[j].handle;
    return h;
  }

  function variants(handle) {
    if (cache[handle]) return Promise.resolve(cache[handle]);
    return fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        if (!d) return null;
        var by = {};
        d.variants.forEach(function (v) {
          var sku = v.sku || '';
          /* SKUs are {handle}-UN-{SIZE} and {handle}-FR-{BLK|WHT|NAT}-{SIZE},
             so the SKU alone says size and colour. Reading the option strings
             instead would depend on their wording. */
          var m = sku.match(/-FR-(BLK|WHT|NAT)-([0-9X]+)$/i);
          if (!m) return;
          var col = { BLK: 'black', WHT: 'white', NAT: 'natural' }[m[1].toUpperCase()];
          var token = m[2].toLowerCase();
          by[col] = by[col] || {};
          by[col][token] = { id: v.id, price: v.price, available: v.available };
        });
        cache[handle] = by;
        return by;
      })
      .catch(function () { return null; });
  }

  /* framed width and height in cm for a slot, orientation applied.
     Unchanged in result, restated in the artifact's terms: the displayed
     print size plus FACE on every edge. 60 x 90 becomes 64 x 94, which is
     Charly's own tape measurement and what FRAMED already held. */
  function dims(slot) {
    var f = FRAMED[slot.size];
    if (!f) return null;
    var p = PRODUCTS.find(function (x) { return x.handle === slot.handle; });
    var landscape = p && p.orientation === 'landscape';
    return landscape ? { w: f[1], h: f[0] } : { w: f[0], h: f[1] };
  }

  /* ------------------------------- the Set Studio's own layout model ----

     Charly, 17 Oct: the Set Studio artifact is the right version. This is
     its layout(), ported rather than reinterpreted, and it checks out
     against what the theme already knew: the artifact's ppc/2 and
     cy*1420 reproduce this repo's pxPerCm and centreLineY exactly for
     five of the six rooms it shared. The sixth is california, which
     Mockup Crea rebuilt after the artifact was made.

     EVERYTHING IS IN THE ARTIFACT'S OWN 2130x2840 SPACE and comes out as
     percentages, so the fact that the plates ship at 1065x1420 never
     enters the arithmetic. That is why the port needs no rescaling and
     cannot drift from it.

     CLEARANCE IS MEASURED, NOT DECLARED. Each room carries a profile: the
     furniture silhouette, one value every two source pixels. For each
     frame the lowest obstruction under its own span is found and the gap
     to the frame's bottom edge is the clearance. So the answer is "56 cm
     above the furniture" or "touches the furniture by 12 cm" for THIS
     frame in THIS room, rather than a fits-or-does-not flag for the room
     as a whole, which is what the theme had. */
  /* FREE PLACEMENT, 18 October. Charly: "we need to be able to move and
     place the photo farmed as we want on the wall".

     A slot may carry px/py, its own top-left on the room's own plate in
     plate pixels. Until someone drags, every slot has none and the row
     is computed exactly as it always was: centred on the room's cx, at
     its cy, gap between each. The moment one frame is dragged, every
     slot is given the position it already had, so nothing jumps and the
     others stay where the eye last saw them.

     The row maths is not deleted and not duplicated. It still runs every
     time, and a placed frame simply overrides its own result, which is
     what keeps "Line them up" a one-line reset rather than a second
     layout engine that can disagree with the first.

     Clearance and the out-of-wall flag are measured per frame from
     wherever the frame actually is, so the chips under the preview stay
     true of a hand-placed set as they were of a row. */
  function layout(st) {
    var r = roomOf(st.room);
    if (!r) return null;
    var slots = st.slots.filter(function (s) { return s.handle && s.size; });
    var ds = slots.map(dims);
    var ok = ds.every(Boolean);
    if (!ok) { ds = ds.filter(Boolean); }
    var gap = gapFor(st.slots, st.room);
    var total = ds.reduce(function (a, d) { return a + d.w; }, 0) + gap * Math.max(0, ds.length - 1);
    var x = r.cx * r.W - total * r.ppc / 2;
    var clear = Infinity, out = false;
    var frames = ds.map(function (d, i) {
      var slot = slots[i];
      var w = d.w * r.ppc, h = d.h * r.ppc;
      /* the row position, always computed; a placed frame overrides it */
      var rowLeft = x, rowTop = r.cy * r.H - h / 2;
      x += w + gap * r.ppc;
      var left = (slot && slot.px != null) ? slot.px : rowLeft;
      var top = (slot && slot.py != null) ? slot.py : rowTop;
      var floor = Infinity;
      var from = Math.max(0, Math.floor(left / 2));
      var to = Math.min(r.profile.length - 1, Math.ceil((left + w) / 2));
      for (var c = from; c <= to; c++) floor = Math.min(floor, r.profile[c]);
      var cl = (floor - (top + h)) / r.ppc;
      clear = Math.min(clear, cl);
      if (left < 0 || left + w > r.W || top < 0 || top + h > r.H) out = true;
      return { left: left, top: top, w: w, h: h, over: cl < 0, slot: slot };
    });
    return { r: r, frames: frames, total: total, clear: clear, out: out, free: !!st.free };
  }

  /* Gives every filled slot the position it is already drawn at, so the
     first drag moves one frame and leaves the rest exactly where they
     were rather than collapsing them back into a row. */
  function seedPlacement() {
    if (!state.free) {
      var L = layout(state);
      if (!L) return;
      L.frames.forEach(function (f) {
        if (!f.slot) return;
        f.slot.px = f.left;
        f.slot.py = f.top;
      });
      state.free = true;
      state.freeRoom = state.room;
    }
    settlePlacement();
  }

  /* 23 Oct 2026, the "frozen third photograph". A print added, or resized,
     once the wall is in free placement had no position of its own
     (px == null). layout() drew it at its row slot, on top of the frames
     already placed, and a drag then started from the plate's top-left
     because baseL was null. The rule now: while the wall is free, every
     filled frame has a legal position, found here, before it is drawn or
     grabbed. Legal means inside the wall and 2 cm clear of every other
     frame. If no such spot exists the wall goes back to a row, which is
     always drawable, and the visitor is told. */
  function fits(r, me, left, top, w, h) {
    if (left < 0 || top < 0 || left + w > r.W || top + h > r.H) return false;
    return !collides(r, me, left, top, w, h);
  }

  function findSpot(r, me, w, h, wantL, wantT) {
    var step = Math.max(2, Math.round(r.ppc * 2));
    var best = null, bestD = Infinity;
    for (var t = 0; t + h <= r.H; t += step) {
      for (var l = 0; l + w <= r.W; l += step) {
        if (!fits(r, me, l, t, w, h)) continue;
        var d = Math.pow(l - wantL, 2) + Math.pow(t - wantT, 2);
        if (d < bestD) { bestD = d; best = { l: l, t: t }; }
      }
    }
    return best;
  }

  function settlePlacement() {
    if (!state.free) return;
    var r = roomOf(state.room);
    if (!r) return;
    /* positions are plate pixels of one room: another room starts again */
    if (state.freeRoom && state.freeRoom !== state.room) { clearPlacement(); return; }
    var L = layout(state);
    if (!L) return;
    var lost = false;
    L.frames.forEach(function (f) {
      var s = f.slot;
      if (!s || lost) return;
      var placed = s.px != null && s.py != null;
      /* a placed frame is left alone unless it now touches a neighbour (it
         was resized). Overhanging the wall is not re-spotted here: a row
         that was drawn too wide stays as drawn, and the drag lets it move
         inward. Half a pixel of tolerance, for the row's own rounding. */
      if (placed && !collides(r, s, s.px + .25, s.py + .25, f.w - .5, f.h - .5)) return;
      var wantL = placed ? s.px : f.left, wantT = placed ? s.py : f.top;
      s.px = null; s.py = null;
      var spot = findSpot(r, s, f.w, f.h, wantL, wantT);
      if (!spot) { lost = true; return; }
      s.px = spot.l; s.py = spot.t;
    });
    if (lost) {
      clearPlacement();
      say('There is no clear space left on this wall for every print, so they are back in a row. '
        + 'A smaller size, or another room, gives them space to move.');
    }
  }

  /* one transient line under the wall, for a move that was refused */
  var sayTimer = 0;
  function say(text) {
    if (!el.msg) return;
    el.msg.textContent = text;
    el.msg.hidden = !text;
    clearTimeout(sayTimer);
    if (text) sayTimer = setTimeout(function () { el.msg.hidden = true; }, 6000);
  }

  function clearPlacement() {
    state.slots.forEach(function (s) { s.px = null; s.py = null; });
    state.free = false;
    state.freeRoom = null;
  }

  /* 2 cm of air between frames, which is Charly's safety margin of 17 Oct
     ("can't be over each other must have 1-2cm spacing safety"), measured
     between the frames' outer edges rather than between the photographs. */
  function collides(r, me, left, top, w, h) {
    var pad = MIN_GAP_CM * r.ppc;
    return state.slots.some(function (s) {
      if (s === me || !s.handle || !s.size || s.px == null) return false;
      var d = dims(s);
      if (!d) return false;
      var ow = d.w * r.ppc, oh = d.h * r.ppc;
      return left < s.px + ow + pad && left + w + pad > s.px &&
             top < s.py + oh + pad && top + h + pad > s.py;
    });
  }

  function roomOf(key) { return ROOMS.rooms[key] || null; }

  /* The artifact ships rooms as an ARRAY; the rest of this file reads them
     by key. Normalised once here, with the asset urls attached, rather
     than two lookups living side by side. */
  function adoptRooms(raw, assets) {
    var byKey = {};
    (raw.rooms || []).forEach(function (r) {
      r.plateUrl = (assets[r.key] || {}).plate || '';
      r.frontUrl = (assets[r.key] || {}).front || '';
      /* the wall actually in view, which is what the artifact's own `out`
         test measures against: the whole plate, not a hand-set figure */
      r.wallCm = r.W / r.ppc;
      byKey[r.key] = r;
    });
    return { gapCm: raw.gapCm, faceCm: raw.faceCm, rooms: byKey,
             keys: (raw.rooms || []).map(function (r) { return r.key; }) };
  }

  /* the prints alone, with no gaps */
  function printsWidthCm() {
    var total = 0;
    state.slots.filter(function (s) { return s.handle && s.size; })
      .forEach(function (s) { var d = dims(s); if (d) total += d.w; });
    return total;
  }

  /* The gap actually used: the preferred one where it fits, closed down to
     the safety floor where it does not, and never past it.

     ONE FUNCTION, TAKING ITS INPUTS, 19 Oct 2026. This used to read the
     global state and layout() did not call it at all: layout() drew every
     row with the fixed 9 cm gap while rowWidthCm(), which chooses the room
     and writes the "these need N cm" note, closed the gap toward 2 cm. A
     set of six large prints was announced as needing 834 cm and drawn at
     869, and a set that "fit" a wall by closing its gaps was drawn wider
     than the wall. The note and the chip printed different widths for the
     same set. layout() now calls this, so the thing that decides a set
     fits and the thing that draws it cannot disagree. */
  function gapFor(slots, roomKey) {
    var filled = slots.filter(function (s) { return s.handle && s.size; });
    var n = filled.length;
    if (n < 2) return GAP_CM;
    var r = roomOf(roomKey);
    if (!r) return GAP_CM;
    var prints = 0;
    filled.forEach(function (s) { var d = dims(s); if (d) prints += d.w; });
    var fitted = (r.wallCm - prints) / (n - 1);
    if (fitted >= GAP_CM) return GAP_CM;
    return Math.max(MIN_GAP_CM, Math.floor(fitted * 10) / 10);
  }
  function gapCm() { return gapFor(state.slots, state.room); }

  function rowWidthCm() {
    var filled = state.slots.filter(function (s) { return s.handle && s.size; });
    if (!filled.length) return 0;
    return printsWidthCm() + gapCm() * (filled.length - 1);
  }

  /* ------------------------------------------------------------- layout */

  /* positions is no longer a constraint on choosing, only on how the
     original render was composed, so fitting is purely about width now. */
  function roomsThatFit(needCm, positions) {
    return ROOMS.keys.filter(function (k) {
      var r = ROOMS.rooms[k];
      return r.wallCm >= needCm;
    });
  }

  /* Charly, 15 Oct: switch room automatically. Prefer the narrowest wall that
     still fits, so the row stays generously placed rather than lost on the
     widest wall available. */
  function autoRoom(needCm, positions) {
    var fit = roomsThatFit(needCm, positions);
    if (!fit.length) return null;
    /* 22 Oct 2026: a wall can be wide enough in centimetres and still push the
       row off the picture, because the row is centred on the room's own hanging
       centre and that is not the middle of the plate. Prefer a room where the
       DRAWN row sits inside the plate; keep the old choice only if none does. */
    var drawn = fit.filter(function (k) { return overhangCm(state.slots, k) === 0; });
    if (drawn.length) fit = drawn;
    if (fit.indexOf(state.room) !== -1) return state.room;
    fit.sort(function (a, b) { return ROOMS.rooms[a].wallCm - ROOMS.rooms[b].wallCm; });
    return fit[0];
  }

  /* How far, in cm, the row as DRAWN in this room runs past the edge of the
     picture (0 when it all sits inside). The same layout() the wall paints
     from, so the warning and the picture cannot disagree. */
  function overhangCm(slots, roomKey) {
    var L = layout({ room: roomKey, slots: slots });
    if (!L || !L.frames || !L.frames.length) return 0;
    var r = L.r, m = 0;
    L.frames.forEach(function (f) {
      m = Math.max(m, -f.left, f.left + f.w - r.W, -f.top, f.top + f.h - r.H);
    });
    return m > 0.5 && r.ppc ? Math.ceil(m / r.ppc) : 0;
  }

  /* DRAGGING A FRAME, 18 October. Charly: "we need to be able to move and
     place the photo farmed as we want on the wall".

     POINTER EVENTS ON THE FRAME, NOT LISTENERS ON THE WINDOW. This site
     has a recorded decision against non-passive wheel and touchmove
     handlers on window, because one of them disables compositor
     scrolling for the whole page even when it returns immediately. None
     is added here: the handlers sit on the frame element, the element
     carries touch-action: none so the browser gives it the gesture
     instead of scrolling, and pointer capture keeps the drag alive if
     the finger leaves the frame. The page scrolls normally everywhere
     else, including on the rest of the stage.

     THE DRAG MOVES A TRANSFORM, NOT left/top. Writing left/top each
     frame relayouts the stage sixty times a second; a transform is
     composited. The real position is written once, on release, and the
     transform is cleared in the same breath.

     IT REFUSES AN OVERLAP RATHER THAN CORRECTING ONE. A frame dragged
     onto another shows it cannot land there and returns to where it
     was. Nudging it to the nearest legal spot was the alternative and
     it is worse: the frame ends up somewhere nobody chose, which on a
     wall measured to the centimetre is the one thing this tool must not
     do. 2 cm of air between frames is his margin of 17 Oct. */
  function attachDrag(node, slot, r, f) {
    node.classList.add('is-draggable');
    node.tabIndex = 0;
    node.setAttribute('role', 'application');
    node.setAttribute('aria-label', 'Move this print on the wall. Arrow keys nudge it a centimetre at a time.');

    var dragging = false, bad = false;
    var startX = 0, startY = 0, baseL = 0, baseT = 0, scale = 1, lastL = 0, lastT = 0;

    /* The wall is the limit, but a frame that already overhangs it (a
       large print on a narrow wall) may still move inward or along the
       free axis. Clamping hard to [0, W - w] pulled it back into its
       neighbour, which then refused the move: the "frozen" frame. */
    function bound(v, base, size, wall) {
      var lo = Math.min(0, base), hi = Math.max(wall - size, base);
      return Math.max(lo, Math.min(hi, v));
    }
    function clampL(v) { return bound(v, baseL, f.w, r.W); }
    function clampT(v) { return bound(v, baseT, f.h, r.H); }

    function tryMove(L, T) {
      baseL = slot.px; baseT = slot.py;
      L = clampL(L); T = clampT(T);
      if (collides(r, slot, L, T, f.w, f.h)) {
        say('That would sit within 2 cm of another print, so it stayed where it was.');
        return false;
      }
      slot.px = L; slot.py = T;
      return true;
    }

    node.addEventListener('pointerdown', function (e) {
      if (e.button) return;
      seedPlacement();
      if (slot.px == null) return;       /* the wall went back to a row */
      baseL = slot.px; baseT = slot.py;
      var box = el.stage.getBoundingClientRect();
      if (!box.width) return;
      scale = r.W / box.width;            /* screen px -> plate px */
      startX = e.clientX; startY = e.clientY;
      lastL = baseL; lastT = baseT;
      dragging = true; bad = false;
      node.setPointerCapture(e.pointerId);
      node.classList.add('is-dragging');
      e.preventDefault();
    });

    node.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      lastL = clampL(baseL + (e.clientX - startX) * scale);
      lastT = clampT(baseT + (e.clientY - startY) * scale);
      bad = collides(r, slot, lastL, lastT, f.w, f.h);
      node.classList.toggle('is-bad', bad);
      node.style.transform = 'translate(' + ((lastL - baseL) / scale) + 'px,' + ((lastT - baseT) / scale) + 'px)';
    });

    function end() {
      if (!dragging) return;
      dragging = false;
      node.classList.remove('is-dragging', 'is-bad');
      node.style.transform = '';
      if (!bad) { slot.px = lastL; slot.py = lastT; say(''); }
      else say('That would sit within 2 cm of another print, so it went back where it was.');
      paint();
    }
    node.addEventListener('pointerup', end);
    node.addEventListener('pointercancel', end);

    /* the same move from the keyboard, because a drag that only a mouse
       or a finger can perform is a feature half the people cannot use */
    node.addEventListener('keydown', function (e) {
      var step = (e.shiftKey ? 10 : 1) * r.ppc;
      var dx = 0, dy = 0;
      if (e.key === 'ArrowLeft') dx = -step;
      else if (e.key === 'ArrowRight') dx = step;
      else if (e.key === 'ArrowUp') dy = -step;
      else if (e.key === 'ArrowDown') dy = step;
      else return;
      e.preventDefault();
      seedPlacement();
      if (slot.px == null) { paint(); return; }
      if (tryMove(slot.px + dx, slot.py + dy)) paint();
    });
  }

  /* ONE RENDERER, used by the live wall and by the kept composition beside
     it. Compare exists because of this: a second wall is the same function
     against different slots, not a second copy of the layout maths that
     could disagree with the first. The gap is passed in rather than read,
     because a kept composition keeps the gap it was made with. */
  function renderWall(wallEl, slots, colour, roomKey, draggable) {
    if (!wallEl) return null;
    var L = layout({ room: roomKey, slots: slots });
    if (!L) return null;
    var r = L.r;
    wallEl.innerHTML = '';
    var dir = r.light === 'right' ? -1 : 1;
    var filled = slots.filter(function (x) { return x.handle && x.size; });

    L.frames.forEach(function (f, i) {
      var slot = filled[i];
      var p = slot && PRODUCTS.find(function (q) { return q.handle === slot.handle; });
      /* no product, no frame: an empty src ships a broken-image box, and a
         handle can be absent because the photograph is held */
      if (!p || !p.img) return;
      var d = dims(slot);
      var node = document.createElement('div');
      node.className = 'cc-setb__hang cc-setb__hang--' + (slot.colour || colour) + (f.over ? ' is-over' : '');
      node.style.left = (f.left / r.W * 100) + '%';
      node.style.top = (f.top / r.H * 100) + '%';
      node.style.width = (f.w / r.W * 100) + '%';
      node.style.height = (f.h / r.H * 100) + '%';
      node.style.setProperty('--sx', (dir * Math.max(2, f.w * 0.012)) + 'px');
      /* THE MOULDING AS A PROPORTION OF ITS OWN FRAME, which is the
         artifact's method and better than the container-relative width
         this had. FACE_CM of moulding on a 150 cm print is a thinner band
         than on a 30 cm one, and expressing it as a percentage of the
         frame makes that true automatically at any viewport. */
      node.style.borderImageWidth = (FRAME_FACE_CM / d.h * 100) + '% ' + (FRAME_FACE_CM / d.w * 100) + '%';
      var art = document.createElement('div');
      art.className = 'cc-setb__art';
      art.style.left = (FRAME_FACE_CM / d.w * 100) + '%';
      art.style.right = (FRAME_FACE_CM / d.w * 100) + '%';
      art.style.top = (FRAME_FACE_CM / d.h * 100) + '%';
      art.style.bottom = (FRAME_FACE_CM / d.h * 100) + '%';
      var img = document.createElement('img');
      img.src = p.img;
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      art.appendChild(img);
      node.appendChild(art);
      if (draggable && slot) attachDrag(node, slot, r, f);
      wallEl.appendChild(node);
    });
    return L;
  }

  /* The furniture drawn back OVER the frames. The artifact ships a cropped
     foreground image per room with its own rect, which replaces masking a
     second copy of the plate: one image instead of two, no mask-image
     support question, and it is what the canonical version does. */
  function paintPlate(plateEl, frontEl, r) {
    if (!r) return;
    if (plateEl) { plateEl.src = r.plateUrl; }
    if (!frontEl) return;
    if (!r.frontUrl) { frontEl.hidden = true; return; }
    frontEl.hidden = false;
    frontEl.src = r.frontUrl;
    frontEl.alt = '';
    frontEl.style.left = (r.front[0] / r.W * 100) + '%';
    frontEl.style.top = (r.front[1] / r.H * 100) + '%';
    frontEl.style.width = ((r.front[2] - r.front[0]) / r.W * 100) + '%';
    frontEl.style.height = ((r.front[3] - r.front[1]) / r.H * 100) + '%';
  }

  /* ------------------------------------------------------------ compare */

  /* Charly, 17 Oct: "Must add the function compare too."

     Both compositions are shown IN THE SAME ROOM, which is the only way the
     comparison means anything: two walls would compare the rooms as much as
     the prints. The kept one keeps its own frame colour and its own gap,
     because those are part of what is being compared, but it is redrawn in
     whatever room is selected now, so changing the room moves both. */
  var kept = null;

  function keepForCompare() {
    kept = {
      slots: state.slots.map(function (s) { return { handle: s.handle, size: s.size, colour: colourOf(s) }; })
        .filter(function (s) { return s.handle && s.size; }),
      colour: state.colour,
      gap: gapCm(),
    };
    renderCompare();
    renderCompareBtn();
  }

  function clearCompare() { kept = null; renderCompare(); renderCompareBtn(); }

  function renderCompare() {
    if (!el.compare) return;
    var r = roomOf(state.room);
    if (!kept || !kept.slots.length || !r) { el.compare.hidden = true; el.compare.innerHTML = ''; return; }
    el.compare.hidden = false;
    if (!el.compare.firstChild) {
      /* Built with the src already on it rather than through innerHTML and
         then assigned: an img that exists for even one frame with no src
         is a broken-image box, and a design check flagged exactly that. */
      var plateEl = document.createElement('img');
      plateEl.className = 'cc-setb__plate';
      plateEl.src = r.plateUrl;
      plateEl.alt = 'The kept composition, in the same room';
      plateEl.decoding = 'async';
      el.compare.appendChild(plateEl);
      var wallEl = document.createElement('div');
      wallEl.className = 'cc-setb__wall';
      wallEl.setAttribute('data-compare-wall', '');
      el.compare.appendChild(wallEl);
      var frontEl = document.createElement('img');
      frontEl.className = 'cc-setb__front';
      frontEl.alt = '';
      frontEl.hidden = true;
      frontEl.setAttribute('aria-hidden', 'true');
      el.compare.appendChild(frontEl);
      var tagEl = document.createElement('span');
      tagEl.className = 'cc-setb__tag';
      tagEl.textContent = 'Kept';
      el.compare.appendChild(tagEl);
    }
    var plate = el.compare.querySelector('.cc-setb__plate');
    var front = el.compare.querySelector('.cc-setb__front');
    paintPlate(plate, front, r);
    plate.alt = 'The kept composition, in the same room';
    renderWall(el.compare.querySelector('[data-compare-wall]'), kept.slots, kept.colour, state.room);
  }

  function renderCompareBtn() {
    if (!el.comparebtn) return;
    el.comparebtn.textContent = kept ? 'Stop comparing' : 'Compare';
    el.comparebtn.setAttribute('aria-pressed', kept ? 'true' : 'false');
    var ready = state.slots.filter(function (s) { return s.handle && s.size; }).length >= 2;
    el.comparebtn.hidden = !ready && !kept;
  }

  function paint() {
    var r = roomOf(state.room);
    if (!r) return;
    paintPlate(el.plate, el.front, r);
    el.plate.alt = '';

    settlePlacement();
    var needCm = rowWidthCm();
    renderWall(el.wall, state.slots, state.colour, state.room, true);
    renderCompare();
    if (el.tidy) el.tidy.hidden = !state.free;
    /* the hint is shown as soon as there is something to drag */
    if (el.hint) el.hint.hidden = !state.slots.some(function (x) { return x.handle && x.size; });
    /* the order arrows only mean something while the prints sit in a row. Once
       one has been dragged every print keeps its own position, so swapping
       two in the list would change nothing on the wall and look broken. */
    if (el.positions) el.positions.classList.toggle('is-free', !!state.free);

    var filled = state.slots.filter(function (s) { return s.handle && s.size; });
    /* the only state that needs explaining to the visitor */
    var positions = Math.max(2, filled.length);
    if (needCm > 0 && !roomsThatFit(needCm, positions).length) {
      el.note.hidden = false;
      var widest = ROOMS.keys.reduce(function (m, k) {
        return Math.max(m, ROOMS.rooms[k].wallCm);
      }, 0);
      el.note.textContent = 'Side by side these need ' + Math.round(needCm) + ' cm of wall, and the '
        + 'widest room I have photographed is ' + Math.round(widest) + ' cm, so the preview cannot show '
        + 'them together. The set is still available and the sizes are right. A smaller size, or turning '
        + 'one of them portrait, will fit.';
    } else {
      /* 22 Oct 2026, Charly: a third print "frozen" in the corner, overlapping
         its neighbour and clipped by the room's edge. It was drawn in the room
         without a word. Now the wall says what is wrong and what to do, and the
         set stays usable: nothing is blocked, the cart button still works. */
      var over = needCm > 0 ? overhangCm(state.slots, state.room) : 0;
      if (over > 0) {
        var alt = ROOMS.keys.filter(function (k) { return k !== state.room && overhangCm(state.slots, k) === 0; })[0];
        el.note.hidden = false;
        el.note.textContent = 'This row runs about ' + over + ' cm past the edge of this wall. '
          + 'Make one print smaller, move one with the arrows or by dragging, '
          + (alt ? 'or try ' + (ROOMS.rooms[alt].title || alt) + ', where it fits.' : 'or take one out.');
      } else {
        el.note.hidden = true;
      }
    }

    renderRooms();
    renderTotal();
    if (onPaint) onPaint();
  }


  /* THE ARTIFACT'S READOUT, ported. Charly asked for the measurement of
     the wall and the visible furniture, and this is the form the Set
     Studio already gives it: a row of short chips that change as the set
     does, rather than a sentence about the room.

     It replaces the hand-set "this wall takes 197 cm" line, and it is
     better for one reason: the clearance is measured against the room's
     own furniture silhouette for THESE frames in THIS position, so it can
     say "56 cm above the furniture" or "touches the furniture by 12 cm"
     rather than declaring whether a room fits in general. It also drops
     the problem that two of those sentences were naming the wrong object:
     "the furniture" is what the profile measures, and it is all it claims.

     Nothing is ever resized to make it fit. A composition too big for a
     room is drawn and flagged, which is Charly's instruction of 16 Oct,
     and the chips are how it is flagged. */
  function renderRoomFact() {
    if (!el.roomfact) return;
    var L = layout(state);
    if (!L) { el.roomfact.textContent = ''; return; }
    el.roomfact.innerHTML = '';
    var chips = [];
    if (L.frames.length) chips.push(['', 'Row ' + Math.round(L.total) + ' cm wide']);
    var c = L.clear;
    if (!L.frames.length) chips.push(['warn', 'Add a photograph']);
    else if (c < 0) chips.push(['bad', 'Touches the furniture by ' + Math.round(-c) + ' cm']);
    else if (c < 15) chips.push(['warn', Math.round(c) + ' cm above the furniture, tight']);
    else if (isFinite(c) && c < 400) chips.push(['ok', Math.round(c) + ' cm above the furniture']);
    if (L.out) chips.push(['bad', 'Wider than the wall in view']);
    chips.forEach(function (ch) {
      var span = document.createElement('span');
      span.className = 'cc-setb__chip' + (ch[0] ? ' is-' + ch[0] : '');
      span.textContent = ch[1];
      el.roomfact.appendChild(span);
    });
  }


  /* ------------------------------------------------------------ controls */

  /* Charly, 17 Oct: "The room option to change must be as a drop down."
     Fifteen chips wrapped to five rows and read as a second copy of the
     composition list above them; one select is one line.

     EVERY ROOM STAYS CHOOSABLE, which was his instruction of 16 Oct ("all
     rooms mockups available") and is why nothing here is disabled. A room
     whose wall is narrower than the set still opens, and says so: the
     width goes in the option's own text, so the warning is readable
     inside the native picker where a title attribute never is, and the
     chips under the preview carry the same fact afterwards.

     There was a second, byte-identical copy of this function below
     renderRoomFact. Function declarations hoist, so the later one silently
     won and the earlier one had never run since the artifact integration.
     Collapsed to one. */
  /* THE ROOM PICKER AS A GRID OF PICTURES, 22 Oct 2026. One button per room
     with its own picture; the chosen one is outlined; a room too narrow for the
     current row says so. It reads from and writes to the hidden select, so there
     is still exactly one source of truth for the room. */
  function drawRoomGrid(need) {
    var grid = root.querySelector('[data-setb-roomgrid]');
    if (!grid) return;
    grid.innerHTML = '';
    ROOMS.keys.forEach(function (k) {
      var r = ROOMS.rooms[k];
      var title = r.title || (k.charAt(0).toUpperCase() + k.slice(1));
      var narrow = need && r.wallCm < need;
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'cc-setb__roomtile' + (k === state.room ? ' is-on' : '') + (narrow ? ' is-narrow' : '');
      b.setAttribute('role', 'radio');
      b.setAttribute('aria-checked', k === state.room ? 'true' : 'false');
      b.setAttribute('aria-label', title + (narrow ? ', too narrow for this row' : ''));
      var shot = document.createElement('span'); shot.className = 'cc-setb__roomshot';
      var im = document.createElement('img');
      im.src = r.plateUrl + (r.plateUrl.indexOf('?') === -1 ? '?' : '&') + 'width=300';
      im.alt = ''; im.loading = 'lazy'; im.decoding = 'async'; im.width = 300; im.height = 400;
      shot.appendChild(im);
      var name = document.createElement('span'); name.className = 'cc-setb__roomname'; name.textContent = title;
      b.appendChild(shot); b.appendChild(name);
      if (narrow) { var n = document.createElement('span'); n.className = 'cc-setb__roomnote'; n.textContent = 'Too narrow'; b.appendChild(n); }
      b.addEventListener('click', function () {
        /* 2 Nov 2026: close the disclosure even when the room did not change.
           A tap that leaves the panel open reads as a tap that did nothing. */
        closeRoomDD();
        if (state.room === k) return;
        el.roomlist.value = k;
        el.roomlist.dispatchEvent(new Event('change', { bubbles: true }));
      });
      grid.appendChild(b);
    });
    /* The summary has to answer "which room am I in" while shut. */
    var now = root.querySelector('[data-setb-roomnow]');
    if (now) {
      var cur = ROOMS.rooms[state.room];
      now.textContent = cur ? (cur.title || (state.room.charAt(0).toUpperCase() + state.room.slice(1))) : '';
    }
  }

  function closeRoomDD() {
    var dd = root.querySelector('[data-setb-roomdd]');
    if (dd) dd.open = false;
  }

  function renderRooms() {
    if (!el.roomlist) return;
    var need = rowWidthCm();
    el.roomlist.innerHTML = '';
    /* 22 Oct 2026: the list was fifteen names in no order (Puglia, Bahia,
       Tropical ... Bondi shack, Valcenis). Two groups now: the composed sets,
       then the rooms made around single photographs. */
    var gSets = document.createElement('optgroup'); gSets.label = 'Sets';
    var gRooms = document.createElement('optgroup'); gRooms.label = 'Rooms by place';
    ROOMS.keys.forEach(function (k) {
      var r = ROOMS.rooms[k];
      var label = r.title || (k.charAt(0).toUpperCase() + k.slice(1));
      if (need && r.wallCm < need) label += '  (' + Math.round(r.wallCm) + ' cm wall)';
      (k.indexOf('hero-') === 0 ? gRooms : gSets).appendChild(new Option(label, k, false, k === state.room));
    });
    el.roomlist.appendChild(gSets);
    if (gRooms.children.length) el.roomlist.appendChild(gRooms);
    drawRoomGrid(need);
    renderRoomFact();
  }

  /* Only figures that were calibrated: the usable wall, and the run the set
     needs against it. Both are relative to the room's own furniture, which
     is what px_per_cm was set by. No absolute heights. */
  /* The sliding indicator is positioned from the checked label rather than
     from an index, so it stays correct if the options ever change number,
     wrap to a second line, or are measured before a web font has settled.
     Called after any colour change and on resize. */
  function moveThumb() {
    if (!el.thumb || !el.frames) return;
    var on = el.frames.querySelector('.cc-frameswitch__input:checked');
    var lab = on && el.frames.querySelector('label[for="' + on.id + '"]');
    if (!lab) return;
    el.thumb.style.width = lab.offsetWidth + 'px';
    el.thumb.style.transform = 'translateX(' + lab.offsetLeft + 'px)';
  }

  function renderTotal() {
    var filled = state.slots.filter(function (s) { return s.handle && s.size && s.variantId; });
    if (filled.length < 2) {
      el.total.textContent = filled.length === 1 ? 'Choose one more photograph.' : '';
      el.add.disabled = true;
      renderAr(filled);
      renderCompareBtn();
      return;
    }
    var cents = filled.reduce(function (a, s) { return a + (s.price || 0); }, 0);
    el.total.textContent = filled.length + ' prints, ' + money(cents);
    el.add.disabled = false;
    renderAr(filled);
    renderCompareBtn();
  }

  /* ---------------------------------------------------------------- AR */

  /* The control appears only when the device can actually open it AND the
     set is complete, and it is REMOVED rather than disabled when either
     stops being true. Support is asked of cc-ar.js so there is one
     definition of it on the site rather than one per section. */
  function renderAr(filled) {
    if (!el.ar || !window.ccAR) return;
    if (filled.length < 2) { el.ar.hidden = true; el.ar.innerHTML = ''; return; }
    el.ar.hidden = false;

    /* DESKTOP GETS THE INVITATION TOO, and it has to carry the set. A code
       pointing at this page alone would open an EMPTY builder on the phone,
       losing the photographs, sizes and colour someone just chose, and a
       feature that quietly discards your work is worse than no feature. So
       the set is written into the address and read back on load, which also
       makes a set a shareable link.

       Rebuilt on every change rather than cached, because the whole point
       is that it matches what is on screen right now. */
    if (!window.ccAR.supported()) {
      el.ar.innerHTML = '';
      var panel = window.ccAR.qrPanel(
        setUrl(),
        root.getAttribute('data-ar-label') || 'See this set on your own wall',
        'Scan this with your phone camera. It opens this set on your phone, where it can be placed on your own wall at its real size.'
      );
      if (panel) el.ar.appendChild(panel); else el.ar.hidden = true;
      return;
    }
    if (el.ar.firstChild && el.ar.querySelector('.cc-ar__btn') && !el.ar.querySelector('.cc-ar__qr')) return;
    el.ar.innerHTML = '';

    var label = root.getAttribute('data-ar-label') || 'See this set on your own wall';
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'cc-btn cc-btn--ghost cc-ar__btn';
    b.textContent = label;
    b.addEventListener('click', function () {
      if (b.disabled) return;
      b.disabled = true;
      b.textContent = 'Building\u2026';
      /* AR puts ONE print on the wall, so it takes that print's own
         colour rather than the set default, which since 17 Oct may not
         be the colour of any print in the set. */
      var lead = state.slots.filter(function (x) { return x.handle && x.size; })[0];
      window.ccAR.open({ items: arItems(), frame: colourOf(lead), gapCm: gapCm() })
        .catch(function () { b.textContent = 'That did not open. Try once more.'; })
        .then(function () {
          window.setTimeout(function () { b.disabled = false; b.textContent = label; }, 2500);
        });
    });
    el.ar.appendChild(b);
  }

  /* set=handle:size,handle:size  frame=black  room=puglia
     Short, readable, and stable across reorderings of the builder's own
     internals: it names what was chosen, not how it is stored. */
  function setUrl() {
    var filled = state.slots.filter(function (s) { return s.handle && s.size; });
    var base = window.ccAR && window.ccAR.qrUrl ? window.ccAR.qrUrl() : location.href;
    var url = base.split('#')[0];
    var join = url.indexOf('?') > -1 ? '&' : '?';
    /* handle:size:colour per print. The old two-part form is still read
       on the way in, so links shared before 17 Oct still open. */
    url += join + 'set=' + encodeURIComponent(filled.map(function (s) { return s.handle + ':' + s.size + ':' + colourOf(s); }).join(','));
    url += '&frame=' + encodeURIComponent(state.colour);
    if (state.room) url += '&room=' + encodeURIComponent(state.room);
    return url + '#cc-setb-h';
  }

  /* Applied once, on load, and only for values that actually exist: a
     handle that is not in the catalogue or a size that is not offered is
     ignored rather than producing an empty slot that looks like a bug. */
  function applyFromUrl() {
    var q = new URLSearchParams(location.search);
    var raw = q.get('set');
    if (!raw) return false;
    var pairs = raw.split(',').map(function (x) { var a = x.split(':'); a[0] = resolveHandle(a[0]); return a; })
      .filter(function (p) {
        return p.length >= 2 && FRAMED[p[1]] && PRODUCTS.some(function (q2) { return q2.handle === p[0]; });
      });
    if (pairs.length < 2) return false;

    var frame = q.get('frame');
    if (frame && ['black', 'white', 'natural'].indexOf(frame) > -1) {
      state.colour = frame;
      var radio = root.querySelector('input[name="cc-setb-frame"][value="' + frame + '"]');
      if (radio) radio.checked = true;
    }
    var room = q.get('room');
    if (room && ROOMS.rooms && ROOMS.rooms[room]) state.room = room;

    state.slots = pairs.slice(0, MAX_SLOTS).map(function (p) {
      var c = COLOURS.indexOf(p[2]) > -1 ? p[2] : null;
      return { handle: p[0], size: p[1], colour: c };
    });
    while (state.slots.length < 2) state.slots.push({});
    return true;
  }

  function arItems() {
    return state.slots.filter(function (s) { return s.handle && s.size && s.variantId; }).map(function (s) {
      var d = dims(s);
      var p = PRODUCTS.find(function (q) { return q.handle === s.handle; });
      /* cc-ar.js adds the 2cm frame face on every edge itself, so it wants
         the PRINT size. dims() is the framed outer, from the same table, so
         subtracting the face keeps one source for both rather than a second
         size table that can drift from the first. */
      return {
        imageUrl: arTexture(p && p.img),
        widthCm: d.w - FRAME_FACE_CM * 2,
        heightCm: d.h - FRAME_FACE_CM * 2,
      };
    });
  }

  /* The payload carries a 300px thumbnail, which is right for a chooser and
     far too soft for something shown at 60cm on a wall. The CDN resizes on
     demand, so the width is simply asked for again. If the URL is ever not
     in that shape the original is used rather than a broken one. */
  function arTexture(url) {
    if (!url) return url;
    var bigger = url.replace(/([?&]width=)\d+/, '$1' + 1000);
    return bigger === url ? url : bigger;
  }

  function money(cents) {
    try {
      return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: window.Shopify && Shopify.currency ? Shopify.currency.active : 'AUD' });
    } catch (e) { return (cents / 100).toFixed(2); }
  }

  function slotRow(i) {
    var s = state.slots[i];
    var wrap = document.createElement('div');
    wrap.className = 'cc-setb__slot';

    /* PLACING THEM WHERE YOU WANT. Charly asked to be able to arrange them
       on the wall. Two buttons per row rather than drag and drop: a drag
       needs pointer capture and an autoscroll to work on a phone, and this
       page already refuses to take over scrolling. Moving left and right
       along a row is the whole of the arrangement anyway, because the hang
       is a single line centred on the wall. */
    var move = document.createElement('div');
    move.className = 'cc-setb__move';
    [['\u2039', -1, 'Swap with the print on the left'], ['\u203a', 1, 'Swap with the print on the right']].forEach(function (spec) {
      var mb = document.createElement('button');
      mb.type = 'button';
      mb.className = 'cc-setb__movebtn';
      mb.textContent = spec[0];
      mb.setAttribute('aria-label', spec[2] + ', photograph ' + (i + 1));
      mb.title = spec[2];
      mb.disabled = (spec[1] < 0 && i === 0) || (spec[1] > 0 && i === state.slots.length - 1);
      mb.addEventListener('click', function () {
        var j = i + spec[1];
        if (j < 0 || j >= state.slots.length) return;
        var tmp = state.slots[i];
        state.slots[i] = state.slots[j];
        state.slots[j] = tmp;
        closeChooser();
        renderSlots();
        paint();
      });
      move.appendChild(mb);
    });

    /* A GRID, NOT A DROPDOWN. Charly, 17 Oct: "The selection of photo must
       be grid with filters by destinations." A select listing 75 titles
       asks someone to choose a photograph by reading its name, on a site
       whose whole argument is that the photograph is the thing. The button
       below opens the shared grid and names what is chosen now. */
    var chosen = s.handle && PRODUCTS.find(function (p) { return p.handle === s.handle; });
    var pick = document.createElement('button');
    pick.type = 'button';
    pick.className = 'cc-setb__pick' + (chosen ? ' is-chosen' : '');
    pick.setAttribute('aria-label', 'Photograph ' + (i + 1));
    pick.setAttribute('aria-expanded', 'false');
    if (chosen) {
      var th = document.createElement('img');
      th.className = 'cc-setb__pickthumb';
      th.src = chosen.img;
      th.alt = '';
      th.loading = 'lazy';
      th.decoding = 'async';
      pick.appendChild(th);
    }
    var pickLabel = document.createElement('span');
    pickLabel.textContent = chosen ? chosen.title : 'Choose a photograph';
    pick.appendChild(pickLabel);
    pick.addEventListener('click', function () { openChooser(i, pick); });

    var size = document.createElement('select');
    size.className = 'cc-setb__size';
    size.setAttribute('aria-label', 'Size ' + (i + 1));
    size.disabled = !s.handle;

    function fillSizes() {
      size.innerHTML = '';
      size.appendChild(new Option('Size', ''));
      var by = cache[s.handle] && cache[s.handle][colourOf(s)];
      ORDER.forEach(function (t) {
        if (!by || !by[t] || !by[t].available) return;
        size.appendChild(new Option(SHOWN[t] + ' cm', t, false, t === s.size));
      });
      size.disabled = false;
    }
    if (s.handle && cache[s.handle]) fillSizes();

    size.addEventListener('change', function () {
      s.size = size.value || null;
      resolveVariant(s);
      /* auto-switch BEFORE painting, so the row is never drawn overflowing */
      var need = rowWidthCm();
      var positions = Math.max(2, state.slots.filter(function (x) { return x.handle && x.size; }).length);
      var next = autoRoom(need, positions);
      if (next && next !== state.room) state.room = next;
      paint();
    });

    /* THE ROW'S OWN FRAME COLOUR. Charly, 17 Oct: "frames colors can be
       customized for each photo mixing frames it's possibles."

       Three dots rather than three pills: the row already carries a
       thumbnail, a title, a size select and two move buttons, and a
       fourth labelled control would push it to a second line on a
       phone. The colour is legible from the dot itself, which is the
       one case where a swatch beats a word.

       The FRAME control above the wall still writes every row at once,
       so "all the same" stays one tap and is still the default. */
    var fr = document.createElement('div');
    fr.className = 'cc-setb__slotframe';
    fr.setAttribute('role', 'radiogroup');
    fr.setAttribute('aria-label', 'Frame colour for photograph ' + (i + 1));
    COLOURS.forEach(function (cname) {
      var on = colourOf(s) === cname;
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'cc-setb__dot cc-setb__dot--' + cname + (on ? ' is-on' : '');
      dot.setAttribute('role', 'radio');
      dot.setAttribute('aria-checked', on ? 'true' : 'false');
      dot.setAttribute('aria-label', cname.charAt(0).toUpperCase() + cname.slice(1) + ' frame');
      dot.disabled = !s.handle;
      dot.addEventListener('click', function () {
        s.colour = cname;
        /* the variant id encodes the colour, so the row has to re-resolve
           or the cart would take the old frame at the new price */
        resolveVariant(s);
        renderSlots();
        paint();
      });
      fr.appendChild(dot);
    });

    wrap.appendChild(move);
    wrap.appendChild(pick);
    wrap.appendChild(size);
    wrap.appendChild(fr);
    if (state.slots.length > 2) {
      var rm = document.createElement('button');
      rm.type = 'button';
      rm.className = 'cc-setb__remove';
      rm.textContent = '\u00d7';
      rm.setAttribute('aria-label', 'Remove photograph ' + (i + 1));
      rm.addEventListener('click', function () {
        state.slots.splice(i, 1);
        closeChooser();
        renderSlots();
        paint();
      });
      wrap.appendChild(rm);
    }
    return wrap;
  }

  function renderSlots() {
    el.positions.innerHTML = '';
    state.slots.forEach(function (_, i) { el.positions.appendChild(slotRow(i)); });
    /* UP TO SIX, not however many positions the room was photographed for.
       positions described how many frames the original render was composed
       with, which is a fact about the photo shoot and not a limit on a wall
       that is measured in centimetres. The real limit is the wall, and the
       gap closing to its floor is what enforces it. */
    if (state.slots.length < MAX_SLOTS) {
      var more = document.createElement('button');
      more.type = 'button';
      more.className = 'cc-setb__more';
      more.textContent = state.slots.length < 3
        ? 'Add a third photograph'
        : 'Add another photograph';
      more.addEventListener('click', function () {
        state.slots.push({ handle: null, size: null, colour: state.colour, variantId: null, price: 0 });
        renderSlots(); paint();
      });
      el.positions.appendChild(more);
    }
  }

  /* ------------------------------------------------------ the chooser */

  var chooserFor = -1, chooserPlace = 'all', chooserOpener = null;

  function placeLabel(p) {
    if (!p) return 'Elsewhere';
    /* "usa" title-cased is "Usa", which reads as a mistake because it is
       one. Anything of three letters or fewer is an abbreviation here. */
    return p.split('-').map(function (w) {
      return w.length <= 3 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1);
    }).join(' ');
  }

  function openChooser(i, opener) {
    if (chooserFor === i && !el.chooser.hidden) { closeChooser(); return; }
    chooserFor = i;
    chooserOpener = opener || null;
    el.chooser.hidden = false;
    if (opener) opener.setAttribute('aria-expanded', 'true');
    renderChooser();
    /* put it directly under the row being filled, so the grid is attached
       to the question it answers rather than floating at the bottom */
    var row = el.positions.children[i];
    if (row && row.nextSibling !== el.chooser) el.positions.insertBefore(el.chooser, row.nextSibling);
  }

  function closeChooser() {
    el.chooser.hidden = true;
    chooserFor = -1;
    root.querySelectorAll('.cc-setb__pick').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
    if (chooserOpener && chooserOpener.focus) chooserOpener.focus();
    chooserOpener = null;
  }

  function renderChooser() {
    el.chooser.innerHTML = '';
    var places = [];
    PRODUCTS.forEach(function (p) {
      var k = p.place || '';
      if (places.indexOf(k) === -1) places.push(k);
    });
    places.sort(function (a, b) { return placeLabel(a).localeCompare(placeLabel(b)); });

    var bar = document.createElement('div');
    bar.className = 'cc-setb__filters';
    bar.setAttribute('role', 'group');
    bar.setAttribute('aria-label', 'Filter by destination');
    [''].concat(places).forEach(function (k, n) {
      var key = n === 0 ? 'all' : k;
      var count = n === 0 ? PRODUCTS.length
        : PRODUCTS.filter(function (p) { return (p.place || '') === k; }).length;
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'cc-setb__filter' + (chooserPlace === key ? ' is-on' : '');
      b.setAttribute('aria-pressed', chooserPlace === key ? 'true' : 'false');
      b.textContent = (n === 0 ? 'All' : placeLabel(k)) + ' (' + count + ')';
      b.addEventListener('click', function () { chooserPlace = key; renderChooser(); });
      bar.appendChild(b);
    });
    el.chooser.appendChild(bar);

    var grid = document.createElement('div');
    grid.className = 'cc-setb__grid';
    var list = chooserPlace === 'all' ? PRODUCTS
      : PRODUCTS.filter(function (p) { return (p.place || '') === chooserPlace; });
    var usedElsewhere = state.slots.map(function (x, n) { return n === chooserFor ? null : x.handle; });

    /* THE MOSAIC. Charly, 17 Oct, exactly: "Left column 1 verticale,
       right column 2 horizontal, then left column 2 horizontal, right
       column 1 vertical, and ongoing alternated with the two horizontal
       fiting facing the vertical on the same row."

       So the unit is not a cell, it is a BLOCK of three: one portrait
       beside two stacked landscapes, and which side the portrait takes
       alternates down the grid. The two landscapes share the portrait's
       height, which is what "fitting facing the vertical" asks for, and
       they get it from the layout rather than from a number: the block
       is a two-column grid whose height the portrait sets, and the
       stack distributes its two cells into that height.

       THE CATALOGUE DOES NOT DIVIDE 1:2. It is 29 portrait to 45
       landscape, and a destination filter can leave two photographs of
       one shape and none of the other. So the blocks are built from
       whatever is left and the remainder is paired same-shape rather
       than left as a hole: a grid that silently drops photographs
       because they did not fit a pattern would be worse than a grid
       that bends at the end. */
    function cellFor(p) {
      var cell = document.createElement('button');
      cell.type = 'button';
      cell.className = 'cc-setb__cell cc-setb__cell--' + (p.orientation === 'portrait' ? 'p' : 'l');
      var taken = usedElsewhere.indexOf(p.handle) > -1;
      if (taken) {
        /* already in this set: shown, never hidden, so the grid does not
           change shape as choices are made, but it says why it is inert */
        cell.classList.add('is-taken');
        cell.disabled = true;
        cell.title = 'Already in this set';
      }
      if (state.slots[chooserFor] && state.slots[chooserFor].handle === p.handle) cell.classList.add('is-on');
      var shot = document.createElement('span');
      shot.className = 'cc-setb__cellshot';
      var im = document.createElement('img');
      im.src = p.img;
      im.alt = '';
      im.loading = 'lazy';
      im.decoding = 'async';
      /* the real shape, so the cell reserves the right box before the
         thumbnail arrives and a mixed column does not reflow as it loads */
      if (p.w && p.h) { im.width = p.w; im.height = p.h; }
      shot.appendChild(im);
      cell.appendChild(shot);
      var cap = document.createElement('span');
      cap.className = 'cc-setb__cellcap';
      cap.textContent = p.title;
      cell.appendChild(cap);
      cell.setAttribute('aria-label', p.title + (p.place ? ', ' + placeLabel(p.place) : ''));
      cell.addEventListener('click', function () { choose(p.handle); });
      return cell;
    }

    var portraits = list.filter(function (p) { return p.orientation === 'portrait'; });
    var landscapes = list.filter(function (p) { return p.orientation !== 'portrait'; });
    var flip = false;

    while (portraits.length && landscapes.length >= 2) {
      var block = document.createElement('div');
      block.className = 'cc-setb__block' + (flip ? ' cc-setb__block--flip' : '');
      var tall = cellFor(portraits.shift());
      var stack = document.createElement('div');
      stack.className = 'cc-setb__stack';
      stack.appendChild(cellFor(landscapes.shift()));
      stack.appendChild(cellFor(landscapes.shift()));
      block.appendChild(tall);
      block.appendChild(stack);
      grid.appendChild(block);
      flip = !flip;
    }

    /* the remainder, paired by shape so each row still lines up */
    var rest = portraits.concat(landscapes);
    for (var n = 0; n < rest.length; n += 2) {
      var pair = document.createElement('div');
      pair.className = 'cc-setb__pair';
      pair.appendChild(cellFor(rest[n]));
      if (rest[n + 1]) pair.appendChild(cellFor(rest[n + 1]));
      grid.appendChild(pair);
    }
    el.chooser.appendChild(grid);

    if (!list.length) {
      var none = document.createElement('p');
      none.className = 'cc-setb__nogrid';
      none.textContent = 'No photographs from there yet.';
      el.chooser.appendChild(none);
    }
  }

  function choose(handle) {
    var s = state.slots[chooserFor];
    if (!s) return;
    s.handle = handle;
    s.size = null; s.variantId = null; s.price = 0;
    variants(handle).then(function () {
      closeChooser();
      renderSlots();
      paint();
    });
  }

  /* ------------------------------------------------- start from one of mine */

  /* Fills the builder from one of Charly's six: room, frame colour, number of
     prints, and each photograph at the size he chose. Variants for every
     product are fetched before anything is painted, so the row never renders
     half-populated and then jumps. */
  /* A SIZE AS THE ARTIFACT WRITES IT, back to the token this store sells.
     The Studio speaks in displayed sizes, "40x60", because that is what a
     customer reads; SKUs and variants speak in the stored tokens, 41x61,
     which are the exact inch conversions and must never be recomputed from
     a rounded label. SHOWN already maps one to the other, so this inverts
     it rather than adding a third table that could disagree. */
  var FROM_SHOWN = (function () {
    var m = {};
    Object.keys(SHOWN).forEach(function (tok) { m[SHOWN[tok].replace(/\s/g, '')] = tok; });
    return m;
  }());
  function tokenOf(shown) { return FROM_SHOWN[String(shown).replace(/\s/g, '')] || null; }

  /* Every room carries its own set in the artifact, so "start from one of
     mine" and "start from this room's set" are the same operation.

     THE CALLER IS THE SECTION ABOVE, NOT A BUTTON IN HERE. The start chips
     were removed on 17 Oct as the duplicate Charly reported, and they were
     also how the set tiles reached this function: cc-diy-page.js found the
     chip for that key and clicked it. A deleted control is a silent way to
     break a working tile, so the builder now listens for an event instead
     and the tiles dispatch it. The busy state moved with it, onto the tile
     that was actually pressed. */
  function setItems(S, R) {
    var list = S ? S.prints : (R && R.start);
    if (!list || !list.length) return [];
    return list
      .map(function (it) {
        var tok = tokenOf(it.size) || (ORDER.indexOf(it.size) !== -1 ? it.size : null);
        return { handle: resolveHandle(it.handle), size: tok, colour: it.colour || null, px: it.px != null ? it.px : null, py: it.py != null ? it.py : null };
      })
      .filter(function (it) { return it.size && PRODUCTS.some(function (p) { return p.handle === it.handle; }); });
  }
  function applySet(key) {
    var S = findSet(key);
    var roomKey = S ? S.room : key;
    var R = roomOf(roomKey);
    if (!R) return;
    var items = setItems(S, R);
    if (!items.length) return;
    root.setAttribute('data-setb-busy', '');
    Promise.all(items.map(function (it) { return variants(it.handle); })).then(function () {
      state.colour = (S && S.colour) || R.colour || state.colour;
      var radio = root.querySelector('input[name="cc-setb-frame"][value="' + state.colour + '"]');
      if (radio) radio.checked = true;
      var placed = items.every(function (it) { return it.px != null && it.py != null; });
      state.free = !!placed;
      state.freeRoom = placed ? roomKey : null;
      state.slots = items.map(function (it) {
        var sl = { handle: it.handle, size: it.size, colour: it.colour || state.colour, px: placed ? it.px : null, py: placed ? it.py : null };
        resolveVariant(sl);
        return sl;
      });
      /* the set was composed in this room, so go there rather than
         auto-switching: it is known to sit correctly on this wall */
      state.room = roomKey;
      track('diy_room_selected', { room_key: roomKey, source: 'set', set_id: S ? S.id : roomKey });
      renderSlots();
      paint();
      moveThumb();
      root.removeAttribute('data-setb-busy');
    });
  }

  document.addEventListener('cc-setb:apply', function (e) {
    if (e && e.detail && e.detail.key) applySet(e.detail.key);
  });


  /* ---------------------------------------------------------------- cart */

  el.add.addEventListener('click', function () {
    var filled = state.slots.filter(function (s) { return s.variantId; });
    if (filled.length < 2) return;
    track('diy_set_add_to_cart', { room_key: state.room, prints: filled.length });
    el.add.disabled = true;
    el.add.textContent = 'Adding…';
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: filled.map(function (s) { return { id: s.variantId, quantity: 1 }; }) }),
    }).then(function (r) {
      if (!r.ok) throw new Error('cart');
      window.location.href = '/cart';
    }).catch(function () {
      el.add.disabled = false;
      el.add.textContent = 'Add the set to the cart';
      el.total.textContent = 'That did not go through. Try once more, or write to me.';
    });
  });

  /* ------------------------------------------------- the composition, as data */
  function currentComposition(name) {
    var filled = state.slots.filter(function (x) { return x.handle && x.size; });
    var title = (name || '').trim() || 'My set';
    return {
      id: slugOf(title),
      title: title,
      room: state.room,
      colour: state.colour,
      prints: filled.map(function (x) {
        var o = { handle: x.handle, size: String(SHOWN[x.size] || x.size).replace(/\s/g, ''), colour: colourOf(x) };
        if (state.free && x.px != null && x.py != null) { o.px = Math.round(x.px); o.py = Math.round(x.py); }
        return o;
      }),
    };
  }

  /* a tile drawn live from a room and a list of prints: the same renderer as
     the wall, with no dragging. This is what lets a set show whichever
     picture of the room is current (the landmark rooms, the day they ship)
     without anyone re-rendering a tile image. */
  function renderTile(host, set) {
    var r = ROOMS && roomOf(set.room);
    if (!host || !r) return false;
    host.innerHTML = '';
    var st = document.createElement('div');
    st.className = 'cc-setb__stage cc-setb__tilestage';
    var plate = document.createElement('img'); plate.className = 'cc-setb__plate'; plate.alt = ''; plate.decoding = 'async';
    var wall = document.createElement('div'); wall.className = 'cc-setb__wall';
    var front = document.createElement('img'); front.className = 'cc-setb__front'; front.alt = ''; front.setAttribute('aria-hidden', 'true'); front.decoding = 'async'; front.hidden = true;
    st.appendChild(plate); st.appendChild(wall); st.appendChild(front);
    host.appendChild(st);
    paintPlate(plate, front, r);
    var placed = (set.prints || []).every(function (it) { return it.px != null && it.py != null; });
    var slots = setItems(set, r).map(function (it) {
      return { handle: it.handle, size: it.size, colour: it.colour || set.colour || 'black', px: placed ? it.px : null, py: placed ? it.py : null };
    });
    renderWall(wall, slots, set.colour || 'black', set.room, false);
    return true;
  }

  function buildComposer() {
    var host = root.querySelector('.cc-setb__foot') || root;
    var box = document.createElement('section');
    box.className = 'cc-setb__compose';
    box.innerHTML =
      '<h3 class="cc-setb__compose-h">Compose mode</h3>' +
      '<p class="cc-setb__compose-p">Only you see this (the address ends ?compose=1). Arrange the wall above, name it, and save it in this browser. It then appears as a tile marked yours on the sets row, for you only. To put it on the site, copy it and send it.</p>' +
      '<label class="cc-setb__compose-l" for="cc-compose-name">Name of the set</label>' +
      '<input class="cc-setb__compose-name" id="cc-compose-name" type="text" maxlength="40" placeholder="Sydney morning">' +
      '<div class="cc-setb__compose-btns">' +
        '<button type="button" class="cc-setb__compare" data-compose-save>Save to my sets</button>' +
        '<button type="button" class="cc-setb__compare" data-compose-copy>Copy this set</button>' +
      '</div>' +
      '<textarea class="cc-setb__compose-out" readonly rows="8" data-compose-out aria-label="This composition as data"></textarea>' +
      '<p class="cc-setb__compose-status" data-compose-status role="status"></p>' +
      '<h4 class="cc-setb__compose-h4">My sets in this browser</h4>' +
      '<ul class="cc-setb__compose-list" data-compose-list></ul>' +
      '<div class="cc-setb__compose-btns"><button type="button" class="cc-setb__compare" data-compose-copyall>Copy all my sets</button></div>';
    host.parentNode.insertBefore(box, host.nextSibling);
    var nameEl = box.querySelector('#cc-compose-name');
    var out = box.querySelector('[data-compose-out]');
    var status = box.querySelector('[data-compose-status]');
    var list = box.querySelector('[data-compose-list]');
    function say(t) { status.textContent = t; }
    function refresh() { out.value = JSON.stringify(currentComposition(nameEl.value), null, 1); }
    function renderList() {
      list.innerHTML = '';
      mySets().forEach(function (st) {
        var li = document.createElement('li');
        li.innerHTML = '<span></span><button type="button" data-load>Load</button><button type="button" data-del>Delete</button>';
        li.firstChild.textContent = st.title + ' (' + (st.prints || []).length + ' prints, ' + st.room + ')';
        li.querySelector('[data-load]').addEventListener('click', function () { nameEl.value = st.title; applySet(st.id); say('Loaded "' + st.title + '". Change it, then Save to my sets.'); });
        li.querySelector('[data-del]').addEventListener('click', function () { saveMySets(mySets().filter(function (x) { return x.id !== st.id; })); renderList(); say('Deleted.'); });
        list.appendChild(li);
      });
    }
    function copy(text, ok) {
      var done = function () { say(ok); };
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(text).then(done, function () { say('Copy blocked: select the text in the box and copy it by hand.'); });
      else { out.select(); try { document.execCommand('copy'); done(); } catch (e) { say('Copy blocked: select the text in the box and copy it by hand.'); } }
    }
    box.querySelector('[data-compose-save]').addEventListener('click', function () {
      var c = currentComposition(nameEl.value);
      if (c.prints.length < 2) { say('Put at least two photographs on the wall first.'); return; }
      var all = mySets().filter(function (x) { return x.id !== c.id; });
      all.push(c); saveMySets(all); renderList();
      say('Saved as "' + c.title + '" (id ' + c.id + '). Same name saves over the old one.');
    });
    box.querySelector('[data-compose-copy]').addEventListener('click', function () { refresh(); copy(out.value, 'Copied this set. Paste it into the conversation to add it to the site.'); });
    box.querySelector('[data-compose-copyall]').addEventListener('click', function () { out.value = JSON.stringify(mySets(), null, 1); copy(out.value, 'Copied all your sets.'); });
    nameEl.addEventListener('input', refresh);
    onPaint = refresh;
    refresh(); renderList();
  }

  window.ccSetb = {
    ready: new Promise(function (r) { readyResolve = r; }),
    renderTile: renderTile,
    apply: applySet,
    compose: COMPOSE,
  };

  /* ---------------------------------------------------------------- boot */

  var setsUrl = root.getAttribute('data-sets');
  var setsP = setsUrl
    ? fetch(setsUrl, { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }).catch(function () { return { sets: [] }; })
    : Promise.resolve({ sets: [] });
  Promise.all([
    fetch(root.getAttribute('data-rooms'), { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }),
    setsP,
  ])
    .then(function (pair) {
      SETS = (pair[1] && pair[1].sets) || [];
      return pair[0];
    })
    .then(function (d) {
      /* asset URLs come from Liquid, which is the only thing that knows a
         Shopify CDN asset path. The first version derived them by stripping
         the filename off the rooms-json URL - correct today, and dependent on
         the shape of a URL nobody here controls. */
      var ASSETS = {};
      try { ASSETS = JSON.parse(root.querySelector('[data-setb-assets]').textContent) || {}; } catch (e) { ASSETS = {}; }
      ROOMS = adoptRooms(d, ASSETS);
      ROOMS.keys = ROOMS.keys.filter(function (k) { return ROOMS.rooms[k] && ROOMS.rooms[k].plateUrl; });
      /* 22 Oct 2026, Charly: "all must have landmark". A room that has not been
         re-made with a landmark through its window or door is not offered until
         it is: its key stays in ROOMS.rooms (an old link still resolves) but it
         leaves the list the picker, autoRoom and the fit check work from. If no
         room has the flag (an old rooms file) nothing is hidden. */
      var lmKeys = ROOMS.keys.filter(function (k) { return ROOMS.rooms[k].landmark; });
      if (lmKeys.length) ROOMS.keys = lmKeys;
      if (!ROOMS.keys.length) throw new Error('no rooms resolved');
      GAP_CM = ROOMS.gapCm || GAP_CM;
      state.room = 'france';
      if (!ROOMS.rooms[state.room]) state.room = ROOMS.keys[0];
      state.slots = [
        { handle: null, size: null, colour: state.colour, variantId: null, price: 0 },
        { handle: null, size: null, colour: state.colour, variantId: null, price: 0 },
      ];
      root.querySelectorAll('input[name="cc-setb-frame"]').forEach(function (i) {
        i.addEventListener('change', function () {
          if (!i.checked) return;
          state.colour = i.value;
          /* This control sets ALL of them, which is what it has always
             done and still the common case. Per-print colour lives on
             the row; this one overwrites every row deliberately, so it
             also works as "put them all back the same". */
          state.slots.forEach(function (s) {
            s.colour = state.colour;
            if (s.handle && s.size) resolveVariant(s);
          });
          renderSlots(); paint();
          moveThumb();
        });
      });
      moveThumb();
      if (el.roomlist) {
        el.roomlist.addEventListener('change', function () {
          state.room = el.roomlist.value;
          track('diy_room_selected', { room_key: state.room, source: 'select' });
          paint();
        });
      }
      if (el.comparebtn) {
        el.comparebtn.addEventListener('click', function () { if (kept) clearCompare(); else keepForCompare(); });
        renderCompareBtn();
      }
      if (el.tidy) {
        el.tidy.addEventListener('click', function () { clearPlacement(); paint(); });
      }
      /* fonts land after first paint and change the label widths under it */
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(moveThumb);
      window.addEventListener('resize', moveThumb, { passive: true });

      /* A SET ARRIVING IN THE ADDRESS. applyFromUrl only sets handles and
         sizes; the prices and variant ids still have to be fetched, the
         same way applySet does it, or the total reads zero and the cart
         button stays dead while the wall looks correct. */
      if (applyFromUrl()) {
        var want = state.slots.filter(function (x) { return x.handle; });
        Promise.all(want.map(function (x) { return variants(x.handle); })).then(function () {
          state.slots.forEach(function (x) { if (x.handle && x.size) resolveVariant(x); });
          renderSlots();
          paint();
          moveThumb();
        });
      }

      /* ONE PRINT ARRIVING FROM A PRODUCT PAGE, 19 Oct 2026. Charly: a DIY Rooms
         button on every product page. /pages/diy-room?photo=<handle> puts that
         print on the wall at 60 x 90, which is the size most people choose, and
         leaves the second place empty for them to fill. Either handle form is
         accepted, as everywhere else, and an unknown one is ignored rather than
         leaving an empty slot that looks like a fault. */
      else {
        var one = new URLSearchParams(location.search).get('photo');
        var photo = one && resolveHandle(one);
        if (photo && PRODUCTS.some(function (p) { return p.handle === photo; })) {
          state.slots[0].handle = photo;
          variants(photo).then(function () {
            var by = cache[photo] && cache[photo][state.colour];
            var size = by && by['61x91'] && by['61x91'].available ? '61x91'
              : ORDER.filter(function (t) { return by && by[t] && by[t].available; })[0];
            if (size) { state.slots[0].size = size; resolveVariant(state.slots[0]); }
            renderSlots(); paint(); moveThumb();
          });
        } else {
          /* 21 Oct 2026: never open on an empty wall. Design review: "Build
             your own" arrived at a bare grey wall and two blank "Choose a
             photograph" fields, with nothing to react to. Charly: "Yes".
             It starts from the first room's own composed pair, which is
             known to sit correctly on that wall, and every part of it can
             be changed. Not applied when a set or a photograph was named
             in the address, which are the two branches above. */
          applySet(state.room);
        }
      }

      renderSlots();
      paint();
      if (COMPOSE) buildComposer();
      if (readyResolve) readyResolve(true);
    })
    .catch(function () { root.setAttribute('data-setb-failed', ''); if (readyResolve) readyResolve(false); });
})();
